import { Bloc } from "@kernel/frontend-react-app";
import { RoomState, defaultRoomState } from "./RoomState";
import {
  ChangeRoomInteractor,
  CreateLocalParticipantInteractor,
  CreateRoomInteractor,
  RestartLocalTrackInteractor,
  StopLocalTrackInteractor,
  ToggleLocalTrackInteractor
} from "./interactors";
import { RoomBloc } from "./RoomBloc";
import { ActiveDevice, Room, Stream } from "@domain";
import { UserType } from "./UserType";

export type RoomVideoBlocState = Pick<RoomState, "room" | "localCreated">;

export type RoomBlocInteractors = {
  createLocalParticipant: CreateLocalParticipantInteractor;
  createRoom: CreateRoomInteractor;
  changeRoom: ChangeRoomInteractor;
  toggleLocalTrack: ToggleLocalTrackInteractor;
  stopLocalTrack: StopLocalTrackInteractor;
  restartLocalTrack: RestartLocalTrackInteractor;
};

export class RoomVideoBloc extends Bloc<RoomVideoBlocState> {
  public constructor(private interactors: RoomBlocInteractors) {
    super({
      room: defaultRoomState.room,
      localCreated: defaultRoomState.localCreated
    });
  }

  public async change(
    videoElement: HTMLVideoElement,
    selectedDevice: ActiveDevice,
    roomName: string,
    type: UserType,
    displayName?: string,
    isScreenSharing: boolean = false
  ): Promise<void> {
    const room = await this.interactors.changeRoom.execute({
      room: this.state.room || Room.createEmpty(roomName),
      videoElement,
      activeDevice: selectedDevice,
      type,
      displayName,
      isScreenSharing
    });
    this.changeState({
      ...this.state,
      localCreated: true,
      room
    });
  }

  public hasAudioEnabled(): boolean {
    if (this.state.room === null) return false;
    const stream = this.state.room.localParticipant?.stream || null;
    if (!stream) return false;
    return stream.hasEnabledTrack("audio");
  }

  public hasVideoEnabled(): boolean {
    if (this.state.room === null) return false;
    const stream = this.state.room.localParticipant?.stream || null;
    if (!stream) return false;
    return stream.hasEnabledTrack("video");
  }

  public toggleLocalAudio(): void {
    if (this.state.room === null) return;
    this.interactors.toggleLocalTrack.execute({
      room: this.state.room,
      type: "audio"
    });
  }

  public toggleLocalVideo(): void {
    if (this.state.room === null) return;
    this.interactors.toggleLocalTrack.execute({
      room: this.state.room,
      type: "video"
    });
  }

  public restart(type: "audio" | "video"): void {
    if (this.state.room === null) return;
    this.interactors.restartLocalTrack.execute({
      room: this.state.room,
      type
    });
  }

  public stop(type: "audio" | "video"): void {
    if (this.state.room === null) return;
    this.interactors.stopLocalTrack.execute({
      room: this.state.room,
      type
    });
  }

  public async createRoom(
    displayName: string,
    type: UserType,
    roomName: string,
    activeDevice: ActiveDevice,
    stream: Stream
  ): Promise<void> {
    const participant = this.interactors.createLocalParticipant.execute({
      displayName,
      type,
      activeDevice,
      stream
    });
    const room = this.interactors.createRoom.execute({
      roomName,
      localParticipant: participant
    });
    this.changeState({
      ...this.state,
      localCreated: true,
      room
    });
  }

  public static create(parent: RoomBloc): RoomVideoBloc {
    const interactors: RoomBlocInteractors = {
      createLocalParticipant: CreateLocalParticipantInteractor.create(),
      createRoom: CreateRoomInteractor.create(),
      changeRoom: ChangeRoomInteractor.create(),
      toggleLocalTrack: ToggleLocalTrackInteractor.create(),
      stopLocalTrack: StopLocalTrackInteractor.create(),
      restartLocalTrack: RestartLocalTrackInteractor.create()
    };
    const bloc = new RoomVideoBloc(interactors);
    bloc.subscribe((state: RoomVideoBlocState) => {
      parent.changeState({
        ...parent.state,
        room: state.room,
        localCreated: state.localCreated
      });
    });
    return bloc;
  }
}
